import React, { useCallback, useState, useMemo, memo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import { Link, Typography, Grid, IconButton, Tooltip } from '@mui/material';
import EditSvg from 'shared/statics/svg/Edit.svg';
import { cloneDeep, uniq, uniqBy, startCase, camelCase } from 'lodash';

import GridGroup from 'shared/components/GridGroup';
import { CardView } from './styled';
import { RowCardDetail } from '../RowCardDetail';
import { RowList } from 'shared/components';
//import RowList from 'shared/components/RpwList';
import SummaryBar from './SummaryBar';
import Checkbox from 'shared/components/Checkbox';

const GroupItem = ({
  groupKey,
  values,
  title,
  itemAmount,
  style,
  onExpand,
  overdueOnlyFilter,
  isVisible,
  togglewNewDonationDrawer,
  toggleImpactReceiptDrawer,
  togglePortfolioDrawer,
  onTitleClick,
  onSelectionChanged,
  selectedRows,
  setSelectedRows,
  group
}) => {
  const handleOnClickNewDonation = useCallback(() => {
    togglewNewDonationDrawer({
      portfolio: (values?.[0].portfolioId || 0)
    });
  }, []);
  const handleSelectionChanged = useCallback(({ api }) => {
    if (onSelectionChanged) {
      onSelectionChanged(groupKey, api.getSelectedRows());
    }
  }, [onSelectionChanged]);

  const selection = selectedRows[groupKey];
  const handleSelectAll = useCallback(() => {
    if (selection?.length === values?.length) {
      setSelectedRows(selectedRows => ({
        ...selectedRows,
        [groupKey]: [],
      }));
    } else {
      setSelectedRows(selectedRows => ({
        ...selectedRows,
        [groupKey]: values
      }));
    }
  }, [selection, values]);

  const onNewDonationClick = () => {
    const initialValues = (values?.[0]?.portfolioId) ? { portfolio: values?.[0]?.portfolioId } : undefined;
    togglewNewDonationDrawer(initialValues);
  };
  const isPortfolioGroup = (group === 'by-portfolio');
  const cardCount = `${itemAmount} Card${itemAmount > 1 ? 's' : ''}`;
  const summaryBar = isPortfolioGroup && <SummaryBar {...{ values, isPortfolioGroup }} />;

  return (
    <div {...{ style }}>
      <GridGroup
        title={`${title} ${overdueOnlyFilter ? '(Overdue Reports Only)' : ''}`}
        itemAmount={summaryBar ?? cardCount}
        onChange={onExpand}
        onTitleClick={isPortfolioGroup ? onTitleClick : undefined}
        onNewDonationClick={onNewDonationClick}
        style={{ height: values.length * 80 + 64 }}
      >
        {isVisible && (
          <CardView>
            <SelectAllContainer>
              <Link
                color="secondary"
                onClick={handleSelectAll}
              >
                {selection?.length === values?.length ? 'Deselect all' : 'Select all'}
              </Link>
            </SelectAllContainer>
            {values?.map(value => (
              <RowCardInner
                key={`${value.portfolioId}_${value?.investmentId || 'NA'}_${value?.allocationId || 'NA'}`}
                {...{
                  value, togglewNewDonationDrawer, togglePortfolioDrawer, toggleImpactReceiptDrawer,
                  selectedRows, setSelectedRows, groupKey, group,
                }}
              />
            ))}
          </CardView>
        )}
      </GridGroup>
    </div>
  );
};

const getManager = (manager) => {
  if (manager?.firstName && manager?.lastName) {
    return `${manager?.firstName} ${manager?.lastName}`;
  }
  if (manager?.firstName) {
    return manager?.firstName;
  }
  if (manager?.lastName) {
    return manager?.lastName;
  }
  return 'No manager set';
};


export const GroupHeader = memo(({
  groupKey,
  values,
  title,
  itemAmount,
  style,
  isExpanded,
  onExpandCollapse,
  onTitleClick,
  selectedRows,
  setSelectedRows,
  group,
}) => {
  const titleTooltip = useMemo(() => {
    switch (group) {
      //      case 'by-manager':
      //        return 'View Manager'
      //      case 'by-npo':
      //        return 'View Impact Receipt'
      case 'by-portfolio':
        return 'Portfolio Settings';
    }
    return '';
  }, [group]);

  const handleSelectAll = useCallback(() => {
    setSelectedRows(selectedRows => ({
      ...selectedRows,
      [groupKey]: selectedRows[groupKey]?.length === values?.length
        ? []
        : values,
    }));
  }, [values]);
  const selection = selectedRows[groupKey];

  const isPortfolioGroup = (group === 'by-portfolio');
  const cardCount = `${itemAmount} Card${itemAmount > 1 ? 's' : ''}`;
  const summaryBar = isPortfolioGroup && <SummaryBar {...{ values }} />;
  const checked = selection?.length === values?.length;
  const indeterminate = !checked && selection?.length > 0;

  const handleEdit = useCallback(event => {
    event.stopPropagation();
    onTitleClick && onTitleClick(event);
  }, [onTitleClick]);

  return (
    <GroupHeaderContainer {...{ style, isExpanded }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <TitleContainer>
          <Bar>
            <Spacer width={3} />
            <SelectAllCheckbox
              {...{ checked, indeterminate }}
              checked={selection?.length === values?.length}
              onChange={handleSelectAll}
            />
            <Title onClick={onExpandCollapse}>
              {title}&nbsp;<TitleCount>({values?.filter(v => v.investmentId)?.length ?? 0})</TitleCount>
              {titleTooltip && isExpanded && (
                <Tooltip title={titleTooltip}>
                  <IconButton
                    onClick={handleEdit}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Title>
          </Bar>
        </TitleContainer>
        {summaryBar ?? cardCount}
      </Grid>
    </GroupHeaderContainer>
  );
});

const GroupHeaderContainer = styled.div`
  padding: 4px;
  border: 1px solid #CBD6E2;
  ${({ isExpanded }) => isExpanded
    ? `border-top-left-radius: 4px;
      border-top-right-radius: 4px;`
    : `border-radius: 4px;
      height: 64px !important;`
  }
  width: calc(100% - 32px) !important;
  display: flex;
  align-items: center;
`;

const Title = styled(Typography).attrs({
  variant: 'h6',
  noWrap: true,
})`
  flex: 1 1;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 16px !important;
  color: #0072A2 !important;
  font-weight: bold !important;
`;

const TitleCount = styled.span`
  color: #778BA5 !important;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 1 1;
`;

export const RowCard = ({
  value, togglewNewDonationDrawer, togglePortfolioDrawer, toggleImpactReceiptDrawer,
  selectedRows, setSelectedRows, groupKey, group, style, isLastRow, defaultLogoUrl,
}) => (
  <div {...{ style }}>
    <RowCardInner {...{
      value, togglewNewDonationDrawer, togglePortfolioDrawer, toggleImpactReceiptDrawer,
      selectedRows, setSelectedRows, groupKey, group, isLastRow, defaultLogoUrl
    }} />
  </div>
);

const RowCardInner = ({
  value, togglewNewDonationDrawer, togglePortfolioDrawer, toggleImpactReceiptDrawer,
  selectedRows, setSelectedRows, groupKey, group, isLastRow, defaultLogoUrl,
}) => (
  <RowCardDetail
    key={value}
    toprow={`${value.grantee?.name ?? '-'} (${value.year ?? 'No year set'})`}
    middlerow={value.programName ?? 'No program set'}
    bottomrow={[value.combinedStatus, startCase(camelCase(value.report?.stage))].filter(x => x).join(' - ')}
    openNewDonationDrawer={togglewNewDonationDrawer}
    openPortfolioDrawer={togglePortfolioDrawer}
    openImpactReceiptDrawer={toggleImpactReceiptDrawer}
    cash={value.cashAmount}
    goods={value.goodsAmount}
    volunteerism={value.volunteerismAmount}
    services={value.servicesAmount}
    progress={value.progress || { value: 0, color: '#ccc' }}
    investmentId={value.investmentId}
    granteeId={value.grantee?.dbId}
    granteeName={value.grantee?.name ?? '-'}
    year={value.year || 'No year set'}
    totalBudgetInitial={value.report?.totalBudgetInitial ?? 0}
    donationMessage={value.message}
    selectedRows={selectedRows}
    setSelectedRows={setSelectedRows}
    group={group}
    groupKey={groupKey}
    taskDistribution={value.taskDistribution}
    investmentTotal={value.investmentTotal}
    allocationAmount={value.allocationAmount}
    claimPercentage={value.claimPercentage}
    totalInvestments={value.totalInvestments}
    totalReports={value.totalReports}
    manager={getManager(value.relationshipManager)}
    managerEmail={value?.relationshipManager?.email ?? ''}
    timelinesTask={value.timelinesTask}
    row={value}
    isLastRow={isLastRow}
    defaultLogoUrl={defaultLogoUrl}
  />
);

GroupItem.propTypes = {
  groupKey: PropTypes.any,
  values: PropTypes.any,
  columns: PropTypes.array,
  view: PropTypes.any,
  title: PropTypes.string,
  style: PropTypes.any,
  onExpand: PropTypes.func,
  overdueOnlyFilter: PropTypes.bool,
  isVisible: PropTypes.bool,
  togglewNewDonationDrawer: PropTypes.func,
  toggleImpactReceiptDrawer: PropTypes.func,
  togglePortfolioDrawer: PropTypes.func,
  onTitleClick: PropTypes.func
};

export default GroupItem;

const groupItemOptions = {
  showTitle: false,
  search: false,
  toolbar: false,
  paging: false,
  padding: 'dense',
  rowStyle: {
    height: 24,
  },
};

const headerHeight = 30;

const ContainerLeft = styled.div`
  margin-top: -30px;
`;


const ContainerCenter = styled.div`
  position: absolute;
  right: 100px;
  top: -30px;
`;

const ContainerRight = styled.div`
  position: absolute;
  right: -12px;
  top: -30px;
`;

const SummaryBarOld = memo(function SummaryBar({
  values,
  isPortfolioGroup,
  summaryBarMode
}) {
  const value = values
    .reduce((all, {
      report,
      cashAmount, goodsAmount, volunteerismAmount, servicesAmount,
      investmentTotal, allocationAmount, claimPercentage, totalReports,
      ...other
    }) => ({
      totalReports: all.totalReports + (report ? 1 : 0),
      investmentTotal: all.investmentTotal + (investmentTotal ?? 0),
      allocationAmount: all.allocationAmount + (allocationAmount ?? 0),
      claimPercentage: all.claimPercentage + (parseFloat(claimPercentage) ?? 0),
      totalAmount: all.totalAmount + (cashAmount ?? 0) + (goodsAmount ?? 0) + (volunteerismAmount ?? 0) + (servicesAmount ?? 0),
      ...other,
    }), {
      totalReports: 0,
      investmentTotal: 0,
      allocationAmount: 0,
      claimPercentage: 0,
      totalAmount: 0,
    });
  const firstManager = values.find(({ relationshipManager }) => relationshipManager);
  const invetments = uniqBy(values, 'investmentId');
  const getInfo = useCallback(() => [
    {
      text: `Donations: ${invetments?.length ?? '--'} (${value.totalReports ?? '--'})`,
      label: 'Donations (Reports)',
      variation: 'inactive',
      details: shortList(uniqBy(values, 'investmentId')
        .map(({ grantee, report }) => report
          ? `${grantee?.name} (${report?.name})`
          : grantee?.name))
    },
  ], [values, firstManager]);
  const [mode, setMode] = useState('INFO');
  const detailsHeader = useMemo(() => {
    switch (summaryBarMode) {
      case 'INFO':
        return getInfo();
      case 'SCHEDULE':
        return cloneDeep(value?.timelinesTask);
      case 'MONEY':
        return [
          {
            text: value.totalAmount && !Number.isNaN(value.allocationAmount)
              ? `Invested: $${numeral(value.totalAmount).format('0a')} (${Math.round(value.allocationAmount * 100 / value.totalAmount)}%)`
              : '--',
            label: 'Invested (Allocated)',
            variation: 'positive',
          },
        ];
    }
  }, [summaryBarMode, value]);

  if (!summaryBarMode) {
    return null;
  }

  return (
    <SummaryBarContainer>
      <RowList content={detailsHeader} />
      <RowList content={value?.taskDistribution} />
    </SummaryBarContainer>
  );
});

const SummaryBarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  max-width: 900px;
  min-width: 200px;

  & > * {
    margin: 0 10px;
  }
`;

const SelectAllContainer = styled.div`
  margin-left: 20px;
`;

const Bar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex: 1 1;
  .MuiCheckbox-root {
    color: #CBD6E2 !important;
  }
  .MuiCheckbox-root.Mui-checked {
    color: #F89915 !important;
  }
`;

const shortList = (list, max = 10) => {
  const unique = uniq(list);

  return unique.length <= max
    ? unique
    : unique
      .slice(0, max)
      .concat([`& ${list.length - max} more...`]);
};

const SelectAllCheckbox = styled(Checkbox).attrs({
  size: 'small',
})`
  .MuiCheckbox-root {
    color: #CBD6E2 !important;
  }
  .MuiCheckbox-root.Mui-checked {
    color: #F89915 !important;
  }
`;

const Spacer = styled.div`
  width: ${({ width }) => width}px;
`;

const EditIcon = styled.img.attrs({
  src: EditSvg,
})`
  margin-left: 10px;
  width: 16px;
  height: 16px;
`;